/**
 * component-wishlist-cards.js — Slice 29 (Wishlist) Phase 3.
 *
 * Shared across every wishlist surface (page P3, drawer P4, mini-cart tab P5,
 * cart-page tab P6). Loaded per-surface (J2 — not in the base bundle).
 *
 * Exposes window.theme.wishlistCards.fill(gridEl, opts) and binds two
 * document-delegated handlers (remove-cross + add-all) once.
 *
 *   - fill: reads window.theme.wishlist (handles, insertion order), fetches each
 *     product through the P1 bridge (/products/{handle}?view=wishlist-card),
 *     extracts the [data-overlay-content] slice (mirrors component-overlay.js
 *     extractContent), and injects standard product cards into the grid.
 *   - remove: [data-wishlist-remove] → wishlist.remove(handle) → drop the card
 *     in place + update count + show empty when the grid empties. (The engine
 *     re-dispatches wishlist:changed itself — we do NOT re-dispatch or re-fill.)
 *   - add-all: [data-wishlist-add-all] → single-variant items batched into ONE
 *     cart_api.add(itemsArray) (cart opens once); multi-variant items left with a
 *     notice (Decision O-3 = Y). Quick-add for the multi items is the loader's
 *     document-delegated quickAdd surface — nothing wired here.
 *
 * Rules: J5 (CustomEvent on document), J10 (await), J12 (dedup guard),
 * reuse window.theme.wishlist / cart_api, locale-aware URLs via Shopify.routes.
 */
(function () {
  if (window.__BLOCKDEDUP_WISHLIST_CARDS__) return;
  window.__BLOCKDEDUP_WISHLIST_CARDS__ = true;

  window.theme = window.theme || {};

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function setCount(el, n) {
    if (!el) return;
    var tpl = n === 1 ? el.getAttribute('data-count-one') : el.getAttribute('data-count-other');
    el.textContent = tpl ? tpl.replace('__COUNT__', String(n)) : String(n);
  }

  function clearNotice(scopeEl) {
    if (!scopeEl) return;
    var notices = scopeEl.querySelectorAll('[data-wishlist-add-all-notice]');
    for (var i = 0; i < notices.length; i++) { notices[i].textContent = ''; notices[i].hidden = true; }
  }

  /* Global Wishlist setting (Theme settings -> Wishlist). Boolean on
     window.theme.settings; undefined/absent = enabled (default on). */
  function removeOnAddEnabled() {
    var s = (window.theme && window.theme.settings) || {};
    return s.wishlist_remove_on_add !== false;
  }

  /* Surgical card drop + count/notice/empty-state reconcile. Shared by the
     remove-cross handler, the add-to-cart listener, and add-all removal.
     Derives grid + scope from the card itself (identical ancestry to deriving
     them from the remove button). */
  function dropCardAndReconcile(card) {
    if (!card) return;
    var grid = card.closest('[data-wishlist-grid]');
    var scope = card.closest('wishlist-page') || (grid ? grid.parentElement : null) || document;
    card.remove();
    var remaining = grid ? grid.querySelectorAll('.product-card').length : 0;
    var countEl = scope.querySelector('[data-wishlist-count]');
    if (countEl) setCount(countEl, remaining);
    clearNotice(scope);
    if (remaining === 0) {
      if (grid) grid.hidden = true;
      var emptyEl = scope.querySelector('[data-wishlist-empty]');
      if (emptyEl) emptyEl.hidden = false;
      var headerEl = scope.querySelector('[data-wishlist-header]');
      var footerEl = scope.querySelector('[data-wishlist-footer]');
      if (headerEl) headerEl.hidden = true;
      if (footerEl) footerEl.hidden = true;
    }
  }

  /* Locate a rendered card by product handle within a scope. Every wishlist
     card carries a remove cross with data-handle; handles are URL-safe slugs
     ([a-z0-9-]) so they are safe in an attribute selector unescaped. */
  function findCardByHandle(scope, handle) {
    if (!scope || !handle) return null;
    var marker = scope.querySelector('[data-wishlist-remove][data-handle="' + handle + '"]');
    return marker ? marker.closest('.product-card') : null;
  }

  /* ---- fill: populate a grid with bridge-rendered standard cards ---- */
  async function fill(gridEl, opts) {
    opts = opts || {};
    var loadingEl = opts.loadingEl;
    var emptyEl = opts.emptyEl;
    var countEl = opts.countEl;
    var headerEl = opts.headerEl;
    var footerEl = opts.footerEl;

    clearNotice(headerEl);
    clearNotice(footerEl);

    var wl = window.theme.wishlist;
    var handles = (wl && wl.getAll()) || [];

    if (handles.length === 0) {
      if (gridEl) gridEl.hidden = true;
      if (loadingEl) loadingEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      setCount(countEl, 0);
      if (headerEl) headerEl.hidden = true;
      if (footerEl) footerEl.hidden = true;
      document.dispatchEvent(new CustomEvent('wishlist:cards:filled', { detail: { count: 0 } }));
      return;
    }

    if (loadingEl) loadingEl.hidden = false;
    if (emptyEl) emptyEl.hidden = true;

    var base = root();
    var htmls = await Promise.all(handles.map(function (h) {
      return fetch(base + 'products/' + encodeURIComponent(h) + '?view=wishlist-card')
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; });
    }));

    var parser = new DOMParser();
    var out = '';
    for (var i = 0; i < htmls.length; i++) {
      if (!htmls[i]) continue; /* deleted / unpublished product — drop, no gap */
      var doc = parser.parseFromString(htmls[i], 'text/html');
      var slice = doc.querySelector('[data-overlay-content]');
      if (slice && slice.innerHTML.trim()) out += slice.innerHTML;
    }

    if (gridEl) {
      gridEl.innerHTML = out;
      gridEl.hidden = false;
    }
    if (loadingEl) loadingEl.hidden = true;

    var rendered = gridEl ? gridEl.querySelectorAll('.product-card').length : 0;
    setCount(countEl, rendered);

    if (rendered === 0) {
      if (gridEl) gridEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      if (headerEl) headerEl.hidden = true;
      if (footerEl) footerEl.hidden = true;
    } else {
      if (headerEl) headerEl.hidden = false;
      if (footerEl) footerEl.hidden = false;
    }

    document.dispatchEvent(new CustomEvent('wishlist:cards:filled', { detail: { count: rendered } }));
  }

  /* ---- delegated remove-cross ---- */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-wishlist-remove]');
    if (!btn) return;
    event.preventDefault();

    var handle = btn.getAttribute('data-handle');
    if (handle && window.theme.wishlist) window.theme.wishlist.remove(handle);

    dropCardAndReconcile(btn.closest('.product-card'));
  });

  /* ---- add-to-cart from the wishlist -> remove + drop the card ----
     Announced by component-quick-add.js: wishlist:product-added { handle } for
     single-variant cards, and via the cart:updated arm for the multi-variant
     dialog. Gated by the global Wishlist setting. This file only loads on the
     wishlist surface, so the event has no listener elsewhere; the page lookup
     guards regardless. */
  document.addEventListener('wishlist:product-added', function (event) {
    var handle = event.detail && event.detail.handle;
    if (!handle) return;
    if (!removeOnAddEnabled()) return;
    /* Surface-agnostic (P4): drop the card wherever it is rendered — page,
       drawer, or both at once (a twin on /pages/wishlist with the drawer open).
       Removing only when a matching card is present preserves the prior
       "wishlist surfaces only" semantics (this file loads only on wishlist
       surfaces; a collection card has no [data-wishlist-remove]). On the page
       the single card is found in the page grid → byte-identical to before. */
    var cards = document.querySelectorAll('[data-wishlist-remove][data-handle="' + handle + '"]');
    if (!cards.length) return;
    if (window.theme.wishlist) window.theme.wishlist.remove(handle);
    cards.forEach(function (m) { dropCardAndReconcile(m.closest('.product-card')); });
  });

  /* ---- delegated add-all ---- */
  document.addEventListener('click', async function (event) {
    var btn = event.target.closest && event.target.closest('[data-wishlist-add-all]');
    if (!btn) return;
    event.preventDefault();

    var scope = btn.closest('wishlist-page, wishlist-drawer, [data-wishlist-tab-panel]') || document;
    var grid = scope.querySelector('[data-wishlist-grid]');
    if (!grid) return;

    var triggers = grid.querySelectorAll('[data-quick-add-trigger]');
    var batch = [];
    var singleHandles = [];
    var multi = 0;
    for (var i = 0; i < triggers.length; i++) {
      var t = triggers[i];
      var count = parseInt(t.getAttribute('data-variant-count'), 10);
      var vid = t.getAttribute('data-variant-id');
      if (isNaN(count) || count <= 1) {
        if (vid) {
          batch.push({ id: Number(vid), quantity: 1 });
          var h = t.getAttribute('data-product-handle');
          if (h) singleHandles.push(h);
        }
      } else {
        multi++;
      }
    }

    var added = false;
    if (batch.length && window.theme.cart_api) {
      try {
        /* one batched POST; cart_api fires cart:updated + auto-opens itself */
        await window.theme.cart_api.add(batch);
        added = true;
      } catch (e) {
        /* surface nothing extra — cart_api throws on a hard failure only */
      }
    }

    /* Remove the batched single-variant items from the wishlist + drop their
       cards (gated by the global Wishlist setting). Multis weren't added, so
       they stay with the notice below. The add-all path never goes through the
       quick-add controller, so it handles its own removals here (not via the
       wishlist:product-added announce). */
    if (added && removeOnAddEnabled()) {
      for (var k = 0; k < singleHandles.length; k++) {
        var sh = singleHandles[k];
        if (window.theme.wishlist) window.theme.wishlist.remove(sh);
        dropCardAndReconcile(findCardByHandle(scope, sh));
      }
    }

    var notice = scope.querySelector('[data-wishlist-add-all-notice]');
    if (notice) {
      if (multi > 0) {
        var tpl = notice.getAttribute('data-notice-template') || '';
        notice.textContent = tpl.replace('__COUNT__', String(multi));
        notice.hidden = false;
      } else {
        notice.hidden = true;
      }
    }
  });

  window.theme.wishlistCards = { fill: fill };

  /* Signal readiness for any surface element that upgraded before this ran. */
  document.dispatchEvent(new CustomEvent('wishlist:cards:ready'));
})();
